/*
  Header navigation behaviour.

  - <nav-drawer>: mobile drawer with a tab bar, accordion groups and a country modal.
  - Desktop: hover (or click without a fine pointer) to open the mega panel, keeping
    the shared backdrop overlay in sync.

  trapFocus / removeTrapFocus come from global.js, which is loaded before this file.
*/

(function () {
  const focusIn = (container, elementToFocus) => {
    if (typeof trapFocus === 'function') trapFocus(container, elementToFocus);
  };

  const focusOut = (elementToFocus) => {
    if (typeof removeTrapFocus === 'function') removeTrapFocus(elementToFocus);
  };

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class NavDrawer extends HTMLElement {
    constructor() {
      super();

      this.drawer = this.querySelector('.nav-drawer');
      this.trigger = this.querySelector('[data-nav-drawer-open]');
      this.rootView = this.querySelector('[data-nav-drawer-root]');
      this.panelsView = this.querySelector('[data-nav-drawer-panels]');
      this.backButtons = Array.from(this.querySelectorAll('[data-nav-drawer-back]'));
      this.tabs = Array.from(this.querySelectorAll('[data-nav-tab]'));
      // Set in the header settings: the drawer opens straight into this tab.
      this.defaultTabIndex = this.tabs.findIndex((tab) => tab.hasAttribute('data-nav-default-tab'));
      this.panels = this.tabs.map((tab) =>
        this.querySelector(`#${tab.getAttribute('aria-controls')}`),
      );
      this.panel = this.querySelector('.nav-drawer__panel');
      this.countryModal = this.querySelector('.model-country-selector');

      this.open = this.open.bind(this);
      this.close = this.close.bind(this);
      this.showRoot = this.showRoot.bind(this);
      this.onKeyUp = this.onKeyUp.bind(this);
      this.warmImages = this.warmImages.bind(this);

      // Pending frame of a slide that has been set up but not started yet.
      this.slideFrame = null;
      this.openFrame = null;
      // Teardown for a pending afterPanelSlide callback, null when none is armed.
      this.panelSlideCleanup = null;
    }

    connectedCallback() {
      if (!this.drawer || !this.trigger) return;

      this.trigger.addEventListener('click', this.open);
      this.addEventListener('keyup', this.onKeyUp);

      this.querySelectorAll('[data-nav-drawer-close]').forEach((button) =>
        button.addEventListener('click', this.close),
      );

      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.selectTab(index));
        tab.addEventListener('keydown', (event) => this.onTabKeyDown(event, index));
      });

      this.backButtons.forEach((button) => button.addEventListener('click', this.showRoot));

      this.querySelectorAll('[data-nav-open-panel]').forEach((button) => {
        button.addEventListener('click', () => {
          const index = this.tabs.findIndex(
            (tab) => tab.getAttribute('aria-controls') === button.dataset.navOpenPanel,
          );
          if (index > -1) this.showPanels(index);
        });
      });

      // A utility link can be set to open the cart drawer. The link keeps its
      // href to the cart page, so this only takes over when the cart drawer is
      // actually on the page.
      this.querySelectorAll('[data-open-cart-drawer]').forEach((link) => {
        link.addEventListener('click', (event) => {
          const cartDrawer = document.querySelector('cart-drawer');
          if (typeof cartDrawer?.open !== 'function') return;

          event.preventDefault();
          this.close();
          // Focus returns to the header cart icon, which is still on screen
          // once this drawer is closed.
          cartDrawer.open(document.querySelector('#cart-icon-bubble') || this.trigger);
        });
      });

      // One country button per view, so bind them all.
      this.querySelectorAll('[data-nav-country-open]').forEach((button) => {
        button.addEventListener('click', () => {
          this.countryOpener = button;
          this.countryModal?.classList.add('modal-open');
          focusIn(this.countryModal, this.countryModal?.querySelector('.close'));
        });
      });

      this.querySelector('[data-nav-country-close]')?.addEventListener('click', () => {
        this.closeCountryModal();
      });

      // The drawer is parked off-screen, so its lazy images only start loading
      // once it opens and the first open shows them dropping in one by one.
      // Warm them when the page is idle, and again on the way into a tap so a
      // viewport that only became drawer sized after load is covered too.
      this.trigger.addEventListener('pointerdown', this.warmImages, { once: true });

      if (this.usesDrawerNav()) {
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(this.warmImages, { timeout: 3000 });
        } else {
          setTimeout(this.warmImages, 2000);
        }
      }
    }

    // Below 1280px the drawer is the navigation; above it, only when the header
    // is set to the drawer menu type.
    usesDrawerNav() {
      return (
        this.dataset.breakpoint === 'desktop' ||
        !window.matchMedia('(min-width: 1280px)').matches
      );
    }

    warmImages() {
      this.querySelectorAll('img[loading="lazy"]').forEach((image) => {
        image.loading = 'eager';
      });
    }

    disconnectedCallback() {
      this.trigger?.removeEventListener('click', this.open);
      this.removeEventListener('keyup', this.onKeyUp);
    }

    open() {
      if (this.drawer.classList.contains('is-open')) return;
      // A frame left over from a tap that never got its slide, so a second tap is
      // never answered with nothing.
      if (this.openFrame) window.cancelAnimationFrame(this.openFrame);

      // The view is put in place before the drawer becomes visible: sliding a
      // view in at the same time as the drawer itself reads as the content
      // sliding over itself, which is what the first open used to look like.
      this.withoutViewTransition(() => {
        if (this.defaultTabIndex > -1) {
          this.showPanels(this.defaultTabIndex, { focus: false, animate: false });
        } else {
          this.showRoot({ focus: false, animate: false });
        }
      });

      // Everything that touches the rest of the page happens first and the slide
      // only starts on the next frame. Locking body scroll is a document wide
      // layout pass, and is-armed renders the drawer with the panel still off to
      // the left so it is laid out and painted before it moves: landing either on
      // the first frame of the transition is what made the drawer shudder in.
      this.drawer.classList.add('is-armed');
      this.trigger.setAttribute('aria-expanded', 'true');
      document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);

      this.openFrame = window.requestAnimationFrame(() => {
        this.openFrame = null;
        this.drawer.classList.add('is-open');

        // The focus trap moves focus into the panel, and the browser answers a
        // focus on something off-screen by scrolling it into view. Waiting for
        // the panel to land keeps that shove out of the animation.
        this.afterPanelSlide(() =>
          focusIn(this.drawer, this.querySelector('[data-nav-drawer-close]')),
        );
      });
    }

    // Runs once the panel has finished sliding, or straight away when there is no
    // transition to wait for. Only one callback is ever armed: opening and closing
    // each replace the other's, so a drawer closed mid-open drops the focus trap
    // open() was still waiting to install. The timer covers a transitionend that
    // never arrives, e.g. the panel being hidden before it lands.
    afterPanelSlide(callback) {
      this.panelSlideCleanup?.();

      if (!this.panel || reducedMotion.matches) {
        callback();
        return;
      }

      const run = () => {
        this.panelSlideCleanup?.();
        callback();
      };

      const onEnd = (event) => {
        if (event.target !== this.panel || event.propertyName !== 'transform') return;
        run();
      };

      const timer = window.setTimeout(run, 700);

      this.panelSlideCleanup = () => {
        window.clearTimeout(timer);
        this.panel.removeEventListener('transitionend', onEnd);
        this.panelSlideCleanup = null;
      };

      this.panel.addEventListener('transitionend', onEnd);
    }

    // Top level list: one row per menu item.
    // The click handler on the back button passes an event, which destructures
    // to the defaults below.
    showRoot({ focus = true, animate = true } = {}) {
      this.rootView?.classList.add('is-active');
      this.querySelectorAll('[data-nav-open-panel]').forEach((button) =>
        button.setAttribute('aria-expanded', 'false'),
      );
      this.scrollViewToTop(this.rootView);

      this.slide(() => {
        this.drawer.classList.remove('is-panel-view');
        this.panelsView?.classList.remove('is-active');

        if (focus) this.querySelector('[data-nav-open-panel], .nav-drawer__root-item')?.focus();
      }, animate);
    }

    // Tabbed view: the mega menu content for one menu item, slid in from the right.
    showPanels(index, { focus = true, animate = true } = {}) {
      // The tab is swapped and the view revealed first, still parked off-screen,
      // so its first paint happens before anything moves rather than inside the
      // slide, see slide().
      this.panelsView?.classList.add('is-active');
      this.selectTab(index, { focus: false });
      this.querySelector(`[data-nav-open-panel="${this.tabs[index]?.getAttribute('aria-controls')}"]`)
        ?.setAttribute('aria-expanded', 'true');

      this.slide(() => {
        this.drawer.classList.add('is-panel-view');
        this.rootView?.classList.remove('is-active');

        if (focus) this.tabs[index]?.focus();
      }, animate);
    }

    // Painting a view and moving it in the same frame means the browser lays out,
    // paints and decodes a whole menu's worth of content on the first frame of
    // the transition: the slide starts late and skips, and the half painted view
    // on top of the one it is replacing is what reads as the content breaking up.
    // Two frames of delay hand the transition a view that is already rasterised.
    slide(update, animate = true) {
      if (this.slideFrame) window.cancelAnimationFrame(this.slideFrame);
      this.slideFrame = null;

      if (!animate || reducedMotion.matches) {
        update();
        return;
      }

      this.slideFrame = window.requestAnimationFrame(() => {
        this.slideFrame = window.requestAnimationFrame(() => {
          this.slideFrame = null;
          update();
        });
      });
    }

    // A view switch and the drawer opening land in the same style recalc, so the
    // slide can only be skipped by committing the new transform in between.
    withoutViewTransition(update) {
      this.drawer.classList.add('is-view-switch-instant');
      update();
      void this.drawer.offsetHeight;
      this.drawer.classList.remove('is-view-switch-instant');
    }

    scrollViewToTop(view) {
      // Each view is its own scroll container. Assigning the offset rather than
      // calling scrollTo keeps the reset out of the slide: a scroll container
      // set to smooth behaviour would otherwise animate it alongside.
      if (view) view.scrollTop = 0;
    }

    closeCountryModal() {
      this.countryModal?.classList.remove('modal-open');
      focusIn(this.drawer, this.countryOpener);
    }

    close() {
      if (!this.drawer.classList.contains('is-open') && !this.openFrame) return;

      // A view switch waiting on its frame would otherwise run while the drawer
      // is on its way out, sliding the views against the panel. The open frame
      // is cancelled for the same reason: a tap that closes the drawer within
      // that frame should not still be answered with a slide in.
      if (this.slideFrame) window.cancelAnimationFrame(this.slideFrame);
      if (this.openFrame) window.cancelAnimationFrame(this.openFrame);
      this.slideFrame = null;
      this.openFrame = null;

      this.countryModal?.classList.remove('modal-open');
      this.drawer.classList.remove('is-open');
      this.trigger.setAttribute('aria-expanded', 'false');
      document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
      focusOut(this.trigger);

      // is-armed carries the compositing hint the slide runs on, so it is only
      // dropped once the panel has finished sliding back out.
      this.afterPanelSlide(() => this.drawer.classList.remove('is-armed'));
    }

    onKeyUp(event) {
      if (event.code.toUpperCase() !== 'ESCAPE') return;

      if (this.countryModal?.classList.contains('modal-open')) {
        this.closeCountryModal();
        return;
      }

      // Escape steps back out one level at a time.
      if (this.drawer.classList.contains('is-panel-view')) {
        this.showRoot();
        return;
      }

      this.close();
    }

    selectTab(index, { focus = true } = {}) {
      this.tabs.forEach((tab, tabIndex) => {
        const isSelected = tabIndex === index;
        tab.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        tab.setAttribute('tabindex', isSelected ? '0' : '-1');
        this.panels[tabIndex]?.toggleAttribute('hidden', !isSelected);
      });

      if (focus) this.tabs[index]?.focus();
      this.scrollViewToTop(this.panelsView);
    }

    onTabKeyDown(event, index) {
      const lastIndex = this.tabs.length - 1;
      let nextIndex = null;

      switch (event.code) {
        case 'ArrowRight':
          nextIndex = index === lastIndex ? 0 : index + 1;
          break;
        case 'ArrowLeft':
          nextIndex = index === 0 ? lastIndex : index - 1;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = lastIndex;
          break;
        default:
          return;
      }

      event.preventDefault();
      this.selectTab(nextIndex);
    }
  }

  if (!customElements.get('nav-drawer')) {
    customElements.define('nav-drawer', NavDrawer);
  }

  // ------------------------------------------------------------ desktop panel

  const overlay = () => document.querySelector('.header-menu-overlay');

  const setOverlay = (isOpen) => {
    overlay()?.classList.toggle('open', isOpen);
  };

  // The panel is fixed to the header's bottom edge, so scrolling the page behind
  // it slides the page out from under a panel that stays put. The page is frozen
  // while a panel is open; the panel keeps its own scrollbar for tall content.
  // The scrollbar width is handed to CSS so removing it does not widen the page.
  const setScrollLock = (isLocked) => {
    const root = document.documentElement;
    if (root.classList.contains('nav-panel-lock') === isLocked) return;

    if (isLocked) {
      const scrollbarWidth = window.innerWidth - root.clientWidth;
      root.style.setProperty('--nav-panel-scrollbar-width', `${scrollbarWidth}px`);
      root.classList.add('nav-panel-lock');
    } else {
      root.classList.remove('nav-panel-lock');
      root.style.removeProperty('--nav-panel-scrollbar-width');
    }
  };

  // The panel is fixed, so it needs the live bottom edge of the header, which
  // moves with the announcement bar, the promo bar and the sticky header.
  // The header's own bottom border is subtracted so the panel's border-top sits
  // on top of it instead of leaving a sub-pixel gap below it.
  const positionPanels = () => {
    const header = document.querySelector('.header-wrapper');
    if (!header) return;

    const borderOffset = header.classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    const bottom = Math.max(0, Math.floor(header.getBoundingClientRect().bottom) - borderOffset);
    document.documentElement.style.setProperty('--nav-panel-top', `${bottom}px`);
  };

  let positionFrame = null;
  const schedulePositionPanels = () => {
    if (positionFrame) return;
    positionFrame = window.requestAnimationFrame(() => {
      positionFrame = null;
      positionPanels();
    });
  };

  positionPanels();
  window.addEventListener('scroll', schedulePositionPanels, { passive: true });
  window.addEventListener('resize', schedulePositionPanels);

  // The sticky header slides its `top` over 0.15s when it reveals, so a panel
  // opened during that reveal is measured against a header still in motion and
  // lands detached from it. Scroll is locked while a panel is open, so this
  // transition is the only thing left that can move the header under it.
  document.querySelector('.section-header')?.addEventListener('transitionend', (event) => {
    if (event.propertyName === 'top') schedulePositionPanels();
  });

  const closeAllPanels = () => {
    document.querySelectorAll('.nav-desktop__details[open]').forEach((details) => {
      details.removeAttribute('open');
      details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    });
    setOverlay(false);
    setScrollLock(false);
  };

  // The panel is a child of <details>, but the gap between the summary and the
  // fixed panel is not, so leaving fires a close that re-entering cancels.
  const CLOSE_DELAY = 220;

  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
  const isDesktop = window.matchMedia('(min-width: 1280px)');
  const hoverEnabled = () => canHover.matches && isDesktop.matches;

  // Panels open on hover where the pointer supports it, on click everywhere else.
  const initDesktopPanels = (root) => {
    root.querySelectorAll('.nav-desktop__details').forEach((details) => {
      const summary = details.querySelector('summary');
      let closeTimer = null;

      const clearTimers = () => {
        clearTimeout(closeTimer);
      };

      details.addEventListener('toggle', () => {
        if (details.open) {
          positionPanels();
          document.querySelectorAll('.nav-desktop__details[open]').forEach((other) => {
            if (other === details) return;
            other.removeAttribute('open');
            other.querySelector('summary')?.setAttribute('aria-expanded', 'false');
          });
        }

        const anyOpen = document.querySelector('.nav-desktop__details[open]') !== null;
        setOverlay(anyOpen);
        setScrollLock(anyOpen);
      });

      // Opens on the event itself: any wait here reads as the panel loading
      // rather than switching, which is what hovering across the bar should feel like.
      details.addEventListener('mouseenter', () => {
        if (!hoverEnabled()) return;
        clearTimers();
        details.setAttribute('open', '');
        summary?.setAttribute('aria-expanded', 'true');
      });

      details.addEventListener('mouseleave', () => {
        if (!hoverEnabled()) return;
        clearTimers();
        closeTimer = setTimeout(() => {
          details.removeAttribute('open');
          summary?.setAttribute('aria-expanded', 'false');
        }, CLOSE_DELAY);
      });

      // A pointer click would toggle shut what hover just opened, so a real
      // mouse click navigates straight to the item's own URL instead. Keyboard
      // activation reports detail === 0, so Enter and Space still toggle.
      summary?.addEventListener('click', (event) => {
        if (!hoverEnabled() || event.detail === 0) return;
        event.preventDefault();

        const url = summary.dataset.navUrl;
        if (url) window.location.href = url;
      });

      summary?.addEventListener('keyup', (event) => {
        if (event.code.toUpperCase() === 'ESCAPE') {
          clearTimers();
          closeAllPanels();
        }
      });
    });
  };

  // ---------------------------------------------------------- header flyouts

  // The help / utility and account dropdowns follow the mega panel: they open on
  // hover where the pointer supports it, and stay click driven everywhere else.
  const initHeaderFlyouts = (root) => {
    const flyouts = root.querySelectorAll(
      '.header__utility-disclosure > details, .header__account-disclosure > details',
    );

    flyouts.forEach((details) => {
      const summary = details.querySelector('summary');
      let closeTimer = null;

      const setOpen = (isOpen) => {
        details.toggleAttribute('open', isOpen);
        summary?.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      };

      details.addEventListener('mouseenter', () => {
        if (!hoverEnabled()) return;
        clearTimeout(closeTimer);
        setOpen(true);
      });

      // The flyout sits 1.6rem below the summary and that gap belongs to neither,
      // so crossing it fires a close that entering the flyout cancels.
      details.addEventListener('mouseleave', () => {
        if (!hoverEnabled()) return;
        clearTimeout(closeTimer);
        closeTimer = setTimeout(() => setOpen(false), CLOSE_DELAY);
      });

      // A pointer click would toggle shut what hover just opened. Keyboard
      // activation reports detail === 0, so Enter and Space still toggle.
      summary?.addEventListener('click', (event) => {
        if (!hoverEnabled() || event.detail === 0) return;
        event.preventDefault();
        // global.js flips aria-expanded on this same click, assuming the toggle
        // went through, so put it back in step with the state it kept.
        summary.setAttribute('aria-expanded', details.hasAttribute('open') ? 'true' : 'false');
      });
    });
  };

  // ------------------------------------------------------ nav card carousel

  // A desktop card row with more than 6 cards scrolls sideways, see
  // snippets/nav-card-row.liquid. Each dot below it is a page of 6 cards (the
  // last page ends flush with the last card), a click on a dot scrolls to that
  // page and the dot nearest the scroll position is the active one. The panel is
  // closed (zero width) on load, so a ResizeObserver refreshes the dots once the
  // panel opens.
  const initNavCardRows = (root) => {
    root.querySelectorAll('[data-nav-cards-scroll]').forEach((row) => {
      if (row.dataset.navCardsReady) return;
      row.dataset.navCardsReady = 'true';

      const track = row.querySelector('[data-nav-cards-track]');
      const dots = Array.from(row.querySelectorAll('[data-nav-cards-dot]'));
      if (!track || !dots.length) return;

      const maxScroll = () => track.scrollWidth - track.clientWidth;

      // A page starts at its first card. The gap is a percentage of the row, so
      // the cards' own positions are used rather than the computed gap.
      const cards = track.children;
      const pageOffset = (index) => {
        const card = cards[index * 6];
        if (!card) return maxScroll();
        return Math.min(card.offsetLeft - cards[0].offsetLeft, maxScroll());
      };

      const update = () => {
        if (track.clientWidth === 0) return;
        const max = maxScroll();
        row.classList.toggle('is-static', max <= 1);
        if (max <= 1) return;

        let active = 0;
        dots.forEach((dot, index) => {
          const distance = Math.abs(track.scrollLeft - pageOffset(index));
          if (distance < Math.abs(track.scrollLeft - pageOffset(active))) active = index;
        });

        dots.forEach((dot, index) => {
          dot.classList.toggle('is-active', index === active);
          dot.setAttribute('aria-current', index === active ? 'true' : 'false');
        });
      };

      dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          track.scrollTo({ left: pageOffset(index), behavior: reducedMotion.matches ? 'auto' : 'smooth' });
        });
      });

      // A mouse can drag the cards themselves; touch and trackpads scroll natively.
      // The drag only starts past a few pixels, so a plain click still follows the
      // card link, and the click that ends a drag is swallowed.
      const DRAG_THRESHOLD = 5;
      let cardDrag = null;
      let suppressClick = false;

      track.addEventListener('pointerdown', (event) => {
        suppressClick = false;
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        cardDrag = { id: event.pointerId, x: event.clientX, scroll: track.scrollLeft, moved: false };
      });

      track.addEventListener('pointermove', (event) => {
        if (!cardDrag || event.pointerId !== cardDrag.id) return;
        const deltaX = event.clientX - cardDrag.x;

        if (!cardDrag.moved) {
          if (Math.abs(deltaX) < DRAG_THRESHOLD) return;
          cardDrag.moved = true;
          row.classList.add('is-dragging');
          track.setPointerCapture(event.pointerId);
        }

        track.scrollLeft = cardDrag.scroll - deltaX;
      });

      const endCardDrag = (event) => {
        if (!cardDrag || event.pointerId !== cardDrag.id) return;
        if (cardDrag.moved) {
          suppressClick = true;
          row.classList.remove('is-dragging');
        }
        if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
        cardDrag = null;
      };
      track.addEventListener('pointerup', endCardDrag);
      track.addEventListener('pointercancel', endCardDrag);

      track.addEventListener(
        'click',
        (event) => {
          if (!suppressClick) return;
          suppressClick = false;
          event.preventDefault();
          event.stopPropagation();
        },
        true,
      );

      // The browser's own link / image drag would take over the gesture.
      track.addEventListener('dragstart', (event) => event.preventDefault());

      track.addEventListener('scroll', update, { passive: true });

      if ('ResizeObserver' in window) {
        new ResizeObserver(update).observe(track);
      } else {
        window.addEventListener('resize', update);
      }

      update();
    });
  };

  initDesktopPanels(document);
  initHeaderFlyouts(document);
  initNavCardRows(document);

  // Clicking the backdrop closes the open panel.
  document.addEventListener('click', (event) => {
    if (event.target.closest('.header-menu-overlay')) closeAllPanels();
  });

  // The theme editor replaces the section markup, which drops the listeners above.
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector('.nav-desktop')) initDesktopPanels(event.target);
    initHeaderFlyouts(event.target);
    initNavCardRows(event.target);
  });
})();
